import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalShipping as LocalShippingIcon,
  RestoreFromTrash as RestoreIcon,
  ShoppingCart as ShoppingCartIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Currency from "../../common/Currency";
import { adminOrderItemService } from "../../../services/adminOrderItemService";
import { AddOrderItemRequest, ItemStatus } from "../../../types/admin";
import { getItemStatusColor, getStatusColor } from "../../../utils/statusColors";

import AddOrderItemModal from "./AddOrderItemModal";
import BulkEditItemFieldsModal from "./BulkEditItemFieldsModal";
import InlineEditCell from "./InlineEditCell";
import OrderItemDetailsModal from "./OrderItemDetailsModal";
import OrderItemEditModal from "./OrderItemEditModal";
import StatusQuickMenu from "./StatusQuickMenu";
import StatusUpdateModal from "./StatusUpdateModal";

interface OrderItem {
  id: string;
  productId: string;
  title: string;
  price: number;
  quantity: number;
  image?: string;
  sku: string;
  subtotal: number;
  retailer?: string;
  itemStatusId?: number;
  itemStatusName?: string;
  itemStatusDisplayName?: string;
  itemStatusColor?: string;
  notes?: string;
  trackingNumber?: string;
  retailerOrderId?: string;
  retailerTrackingId?: string;
  flightNumber?: string;
  estimatedDelivery?: string;
  orderedAt?: string;
  shippedAt?: string;
  estimatedWeight?: number;
  cargoWeight?: number;
  itemCarrierCargoFee?: number;
  cargoPrice?: number;
  sellerName?: string;
  sellerId?: string;
  offerId?: string;
  sellerShippingFee?: number;
  priceTjs?: number | null;
  subtotalTjs?: number | null;
  isDeleted?: boolean;
  hasTrackingNumber?: boolean;
  hasNotes?: boolean;
  isShipped?: boolean;
  isOrdered?: boolean;
}

interface OrderItemsTableProps {
  items: OrderItem[];
  orderId: string;
  onEditItem: (item: OrderItem) => void;
  onViewItem: (item: OrderItem) => void;
  onStatusUpdate: (
    orderItemId: string,
    newStatusId: number,
    notes?: string,
    sendNotification?: boolean
  ) => Promise<void>;
  onUpdateItem: (
    orderItemId: string,
    updates: Partial<OrderItem>
  ) => Promise<void>;
  onAddItem: (request: AddOrderItemRequest) => Promise<void>;
  onDeleteItem: (orderItemId: string) => Promise<void>;
  onRestoreItem?: (orderItemId: string) => Promise<void>;
  onBulkStatusUpdate?: () => void;
  // Optional: external selection for cross-order bulk operations
  externalSelectedIds?: Set<string>;
  onExternalSelectionChange?: (itemId: string, selected: boolean) => void;
  onExternalSelectAll?: (itemIds: string[], selected: boolean) => void;
  // Optional: highlight items matching a specific status
  highlightStatusId?: number;
  // Optional: item-level filters for dimming non-matching items
  retailerFilter?: string;
  retailerOrderIdFilter?: "has" | "notset" | "";
  retailerTrackingIdFilter?: "has" | "notset" | "";
}

const OrderItemsTable: React.FC<OrderItemsTableProps> = ({
  items,
  orderId,
  onEditItem,
  onViewItem,
  onStatusUpdate,
  onUpdateItem,
  onAddItem,
  onDeleteItem,
  onRestoreItem,
  onBulkStatusUpdate,
  externalSelectedIds,
  onExternalSelectionChange,
  onExternalSelectAll,
  highlightStatusId,
  retailerFilter,
  retailerOrderIdFilter,
  retailerTrackingIdFilter,
}) => {
  const { t } = useTranslation();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OrderItem | null>(null);
  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>([]);
  const [bulkStatusId, setBulkStatusId] = useState<number | "">("");
  const [itemStatuses, setItemStatuses] = useState<ItemStatus[]>([]);
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<OrderItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Quick action state
  const [quickActionLoading, setQuickActionLoading] = useState<string | null>(null);
  const [shippedPopoverAnchor, setShippedPopoverAnchor] = useState<HTMLElement | null>(null);
  const [shippedItemId, setShippedItemId] = useState<string | null>(null);
  const [shippedTrackingNumber, setShippedTrackingNumber] = useState("");

  // Use external or internal selection
  const useExternalSelection = !!externalSelectedIds;
  const selectedItemIds = useExternalSelection
    ? items.filter((item) => externalSelectedIds!.has(item.id)).map((item) => item.id)
    : internalSelectedIds;

  useEffect(() => {
    loadItemStatuses();
  }, []);

  const loadItemStatuses = async () => {
    try {
      const statuses = await adminOrderItemService.getAvailableItemStatuses();
      setItemStatuses(statuses);
    } catch {
      // Non-critical
    }
  };

  const handleStatusClick = (item: OrderItem) => {
    setSelectedItem(item);
    setStatusModalOpen(true);
  };

  const handleEditClick = (item: OrderItem) => {
    setSelectedItem(item);
    setEditModalOpen(true);
  };

  const handleDetailsClick = (item: OrderItem) => {
    setSelectedItem(item);
    setDetailsModalOpen(true);
  };

  const handleStatusUpdate = async (
    orderItemId: string,
    newStatusId: number,
    notes?: string,
    sendNotification?: boolean
  ) => {
    await onStatusUpdate(orderItemId, newStatusId, notes, sendNotification);
  };

  const handleItemUpdate = async (
    orderItemId: string,
    updates: Partial<OrderItem>
  ) => {
    await onUpdateItem(orderItemId, updates);
  };

  const handleSelectItem = (itemId: string) => {
    if (useExternalSelection) {
      const isSelected = externalSelectedIds!.has(itemId);
      onExternalSelectionChange?.(itemId, !isSelected);
    } else {
      setInternalSelectedIds((prev) =>
        prev.includes(itemId)
          ? prev.filter((id) => id !== itemId)
          : [...prev, itemId]
      );
    }
  };

  const handleSelectAll = () => {
    if (useExternalSelection) {
      const allSelected = items.every((item) => externalSelectedIds!.has(item.id));
      onExternalSelectAll?.(
        items.map((item) => item.id),
        !allSelected
      );
    } else {
      if (internalSelectedIds.length === items.length) {
        setInternalSelectedIds([]);
      } else {
        setInternalSelectedIds(items.map((item) => item.id));
      }
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatusId || selectedItemIds.length === 0) return;

    try {
      setBulkLoading(true);
      await adminOrderItemService.bulkUpdateItemStatus({
        orderItemIds: selectedItemIds,
        newItemStatusId: Number(bulkStatusId),
        sendNotification: true,
      });
      setSnackbar({
        open: true,
        message: t("admin.orders.bulkStatusUpdateSuccess", { count: selectedItemIds.length }),
        severity: "success",
      });
      if (useExternalSelection) {
        // Let parent handle clearing
      } else {
        setInternalSelectedIds([]);
      }
      setBulkStatusId("");
      onBulkStatusUpdate?.();
    } catch {
      setSnackbar({
        open: true,
        message: t("admin.orders.bulkStatusUpdateFailed"),
        severity: "error",
      });
    } finally {
      setBulkLoading(false);
    }
  };

  const handleDeleteClick = (item: OrderItem) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      setDeleteLoading(true);
      await onDeleteItem(itemToDelete.id);
      setSnackbar({
        open: true,
        message: t("admin.orders.itemDeleted"),
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: t("admin.orders.failedToDeleteItem"),
        severity: "error",
      });
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  // Quick action: Mark as Ordered
  const handleMarkAsOrdered = useCallback(async (item: OrderItem) => {
    try {
      setQuickActionLoading(item.id);
      await adminOrderItemService.markAsOrdered(item.id);
      setSnackbar({
        open: true,
        message: t("admin.orders.markedAsOrdered"),
        severity: "success",
      });
      onBulkStatusUpdate?.();
    } catch {
      setSnackbar({
        open: true,
        message: t("admin.orders.failedToUpdateStatus"),
        severity: "error",
      });
    } finally {
      setQuickActionLoading(null);
    }
  }, [onBulkStatusUpdate, t]);

  // Quick action: Mark as Shipped (open popover)
  const handleMarkAsShippedClick = (event: React.MouseEvent<HTMLElement>, item: OrderItem) => {
    event.stopPropagation();
    setShippedPopoverAnchor(event.currentTarget);
    setShippedItemId(item.id);
    setShippedTrackingNumber("");
  };

  const handleMarkAsShippedConfirm = async () => {
    if (!shippedItemId) return;
    try {
      setQuickActionLoading(shippedItemId);
      await adminOrderItemService.markAsShipped(shippedItemId, {
        trackingNumber: shippedTrackingNumber,
      });
      setSnackbar({
        open: true,
        message: t("admin.orders.markedAsShipped"),
        severity: "success",
      });
      setShippedPopoverAnchor(null);
      setShippedItemId(null);
      setShippedTrackingNumber("");
      onBulkStatusUpdate?.();
    } catch {
      setSnackbar({
        open: true,
        message: t("admin.orders.failedToUpdateStatus"),
        severity: "error",
      });
    } finally {
      setQuickActionLoading(null);
    }
  };

  // Inline edit save handler
  const handleInlineFieldSave = async (itemId: string, field: string, value: string) => {
    try {
      await onUpdateItem(itemId, { [field]: value || undefined });
      setSnackbar({
        open: true,
        message: t("admin.orders.itemUpdatedSuccessfully"),
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: t("admin.orders.failedToUpdateItem"),
        severity: "error",
      });
      throw new Error("Update failed");
    }
  };

  if (!items || items.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("admin.orders.orderItems")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("admin.orders.noItemsFound")}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center" }}
            >
              {t("admin.orders.orderItemsCount", { count: items.length })}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAddItemModalOpen(true)}
            >
              {t("admin.orders.addItem")}
            </Button>
          </Box>

          {/* Bulk Actions Bar (internal selection only - external uses CrossOrderBulkBar) */}
          {!useExternalSelection && selectedItemIds.length > 0 && (
            <Paper
              sx={{
                p: 2,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                backgroundColor: "#e3f2fd",
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                {t("admin.orders.itemsSelected", { count: selectedItemIds.length })}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t("admin.orders.updateItemStatus")}</InputLabel>
                <Select
                  value={bulkStatusId}
                  onChange={(e) => setBulkStatusId(e.target.value as number)}
                  label={t("admin.orders.updateItemStatus")}
                  disabled={bulkLoading}
                >
                  {itemStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: getItemStatusColor(status),
                          }}
                        />
                        <Typography variant="body2">{status.displayName}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="small"
                onClick={handleBulkStatusUpdate}
                disabled={bulkLoading || !bulkStatusId}
              >
                {bulkLoading ? t("admin.orders.updating") : t("admin.orders.bulkUpdateStatus")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setBulkEditModalOpen(true)}
                disabled={bulkLoading}
              >
                {t("admin.orders.bulkEdit")}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setInternalSelectedIds([]);
                  setBulkStatusId("");
                }}
                disabled={bulkLoading}
              >
                {t("common.cancel")}
              </Button>
            </Paper>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedItemIds.length === items.length && items.length > 0}
                      indeterminate={selectedItemIds.length > 0 && selectedItemIds.length < items.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>{t("admin.orders.product")}</TableCell>
                  <TableCell>{t("admin.orders.sku")}</TableCell>
                  <TableCell align="right">{t("common.price")}</TableCell>
                  <TableCell align="right">{t("admin.orders.quantity")}</TableCell>
                  <TableCell align="right">{t("common.subtotal")}</TableCell>
                  <TableCell align="center">{t("admin.orders.status")}</TableCell>
                  <TableCell align="center">{t("admin.orders.retailer")}</TableCell>
                  <TableCell align="center">{t("admin.orders.retailerOrderId")}</TableCell>
                  <TableCell align="center">{t("admin.orders.retailerTrackingId")}</TableCell>
                  <TableCell align="center">{t("admin.orders.flightNumber")}</TableCell>
                  <TableCell align="center">{t("common.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => {
                  const isSelected = useExternalSelection
                    ? externalSelectedIds!.has(item.id)
                    : internalSelectedIds.includes(item.id);
                  const statusDimmed = highlightStatusId !== undefined && item.itemStatusId !== highlightStatusId;
                  const retailerDimmed = !!retailerFilter && !(item.retailer || "").toLowerCase().includes(retailerFilter.toLowerCase());
                  const orderIdDimmed = retailerOrderIdFilter === "has" ? !item.retailerOrderId : retailerOrderIdFilter === "notset" ? !!item.retailerOrderId : false;
                  const trackingIdDimmed = retailerTrackingIdFilter === "has" ? !item.retailerTrackingId : retailerTrackingIdFilter === "notset" ? !!item.retailerTrackingId : false;
                  const isDimmed = statusDimmed || retailerDimmed || orderIdDimmed || trackingIdDimmed;

                  return (
                    <TableRow
                      key={item.id || index}
                      selected={isSelected}
                      sx={{
                        ...(isDimmed && {
                          opacity: 0.4,
                          transition: "opacity 0.2s",
                          "&:hover": { opacity: 0.7 },
                        }),
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectItem(item.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.title}
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: "cover",
                                marginRight: 12,
                              }}
                            />
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {item.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {item.productId}
                            </Typography>
                            {item.sellerName && (
                              <Typography variant="caption" display="block" sx={{ color: "info.main" }}>
                                {t("admin.orders.seller")}: {item.sellerName}
                                {item.sellerShippingFee != null && item.sellerShippingFee > 0 && (
                                  <> · <Currency amount={item.sellerShippingFee} /> {t("admin.orders.sellerShipFee")}</>
                                )}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {item.sku && item.retailer?.toLowerCase() === "amazon" ? (
                          <a
                            href={`https://www.amazon.com/dp/${item.sku}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1976d2", textDecoration: "none" }}
                            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                          >
                            {item.sku}
                          </a>
                        ) : (
                          item.sku
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Currency amount={item.price} />
                      </TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        <Currency amount={item.subtotal} />
                      </TableCell>
                      <TableCell align="center">
                        <StatusQuickMenu
                          itemId={item.id}
                          currentStatusId={item.itemStatusId}
                          currentStatusName={item.itemStatusName}
                          currentStatusDisplayName={item.itemStatusDisplayName}
                          onQuickStatusUpdate={handleStatusUpdate}
                          onOpenFullModal={() => handleStatusClick(item)}
                          getStatusColor={getStatusColor}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {item.retailer || t("admin.orders.na")}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <InlineEditCell
                          value={item.retailerOrderId || ""}
                          onSave={(val) => handleInlineFieldSave(item.id, "retailerOrderId", val)}
                          placeholder={t("admin.orders.notSet")}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <InlineEditCell
                          value={item.retailerTrackingId || ""}
                          onSave={(val) => handleInlineFieldSave(item.id, "retailerTrackingId", val)}
                          placeholder={t("admin.orders.notSet")}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <InlineEditCell
                          value={item.flightNumber || ""}
                          onSave={(val) => handleInlineFieldSave(item.id, "flightNumber", val)}
                          placeholder={t("admin.orders.notSet")}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          {/* Quick Action: Mark as Ordered (visible when status = Order Placed, ID 1) */}
                          {item.itemStatusId === 1 && (
                            <Tooltip title={t("admin.orders.markAsOrdered")}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsOrdered(item);
                                  }}
                                  disabled={quickActionLoading === item.id}
                                >
                                  <ShoppingCartIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {/* Quick Action: Mark as Shipped (visible when status = Purchased, ID 2) */}
                          {item.itemStatusId === 2 && (
                            <Tooltip title={t("admin.orders.markAsShipped")}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => handleMarkAsShippedClick(e, item)}
                                  disabled={quickActionLoading === item.id}
                                >
                                  <LocalShippingIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title={t("admin.orders.viewItemDetails")}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDetailsClick(item)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("admin.orders.editItem")}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditClick(item)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {item.isDeleted && onRestoreItem ? (
                            <Tooltip title={t("admin.orders.restoreItem")}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => onRestoreItem(item.id)}
                              >
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title={t("admin.orders.deleteItem")}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(item)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Mark as Shipped Popover */}
      <Popover
        open={Boolean(shippedPopoverAnchor)}
        anchorEl={shippedPopoverAnchor}
        onClose={() => {
          setShippedPopoverAnchor(null);
          setShippedItemId(null);
          setShippedTrackingNumber("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, minWidth: 280 }}>
          <Typography variant="subtitle2">{t("admin.orders.markAsShipped")}</Typography>
          <TextField
            size="small"
            label={t("admin.orders.enterTrackingNumber")}
            value={shippedTrackingNumber}
            onChange={(e) => setShippedTrackingNumber(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && shippedTrackingNumber.trim()) {
                handleMarkAsShippedConfirm();
              }
            }}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            fullWidth
          />
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={() => {
                setShippedPopoverAnchor(null);
                setShippedItemId(null);
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleMarkAsShippedConfirm}
              disabled={!shippedTrackingNumber.trim() || quickActionLoading === shippedItemId}
            >
              {t("common.confirm")}
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Status Update Modal (fallback from StatusQuickMenu "More options...") */}
      <StatusUpdateModal
        open={statusModalOpen}
        onClose={() => {
          setStatusModalOpen(false);
          setSelectedItem(null);
        }}
        orderItem={
          selectedItem
            ? {
                id: selectedItem.id,
                title: selectedItem.title,
                currentStatusId: selectedItem.itemStatusId,
                currentStatusName:
                  selectedItem.itemStatusName ||
                  selectedItem.itemStatusDisplayName,
              }
            : null
        }
        onStatusUpdate={handleStatusUpdate}
      />

      {/* Edit Item Modal */}
      <OrderItemEditModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedItem(null);
        }}
        orderItem={selectedItem}
        onSave={handleItemUpdate}
      />

      {/* Item Details Modal */}
      <OrderItemDetailsModal
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedItem(null);
        }}
        orderItemId={selectedItem?.id || ""}
      />

      {/* Bulk Edit Fields Modal */}
      <BulkEditItemFieldsModal
        open={bulkEditModalOpen}
        onClose={() => setBulkEditModalOpen(false)}
        selectedItemIds={selectedItemIds}
        onSuccess={() => {
          setSnackbar({
            open: true,
            message: t("admin.orders.bulkEditSuccess", { count: selectedItemIds.length }),
            severity: "success",
          });
          if (!useExternalSelection) {
            setInternalSelectedIds([]);
          }
          setBulkStatusId("");
          onBulkStatusUpdate?.();
        }}
      />

      {/* Add Order Item Modal */}
      <AddOrderItemModal
        open={addItemModalOpen}
        onClose={() => setAddItemModalOpen(false)}
        orderId={orderId}
        onSave={async (request) => {
          await onAddItem(request);
          setSnackbar({
            open: true,
            message: t("admin.orders.itemAdded"),
            severity: "success",
          });
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setItemToDelete(null);
        }}
      >
        <DialogTitle>{t("admin.orders.deleteItem")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("admin.orders.deleteItemConfirm")}
          </DialogContentText>
          {itemToDelete && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: "bold" }}>
              {itemToDelete.title}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setItemToDelete(null);
            }}
            disabled={deleteLoading}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? t("admin.orders.deleting") : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OrderItemsTable;
